import { defineStyle, defineStyleConfig, ComponentStyleConfig } from '@chakra-ui/react';
import { transparentize } from '@chakra-ui/theme-tools'; // transparentize appears to be replaced with colorMix in chakra v3, currently still in development

const baseStyle = defineStyle(props => {
  const { colorScheme } = props;

  return {
    alignItems: 'center',
    borderRadius: 'base',
    display: 'inline-flex',
    fontWeight: 'medium',
    gap: 1,
    px: 2,
    textTransform: 'none',

    '.utility-icon': {
      color: `${colorScheme}.400`,

      _dark: {
        color: `${colorScheme}.300`,
        opacity: '0.8',
      },
    },
  };
});

const sizes = {
  xs: {
    fontSize: 'xs',
    lineHeight: 4,
    gap: 0.25,
    px: 1,
    py: 0.5,
  },
  sm: {
    fontSize: 'sm',
    lineHeight: 4,
    gap: 0.5,
    px: 1.5,
    py: 1,
  },
  md: {
    fontSize: 'md',
    lineHeight: 5,
    px: 2,
    py: 1,
  },
  lg: {
    fontSize: 'lg',
    lineHeight: 6,
    px: 2.5,
    py: 1,
  },
  xl: {
    fontSize: 'xl',
    lineHeight: 7,
    gap: 1.5,
    px: 3,
    py: 1,
  },
};

const pillCommon = {
  borderRadius: 'full',
  flexShrink: '0',
  justifyContent: 'center',
  px: 1,
  py: 0,
  minWidth: 4,
};

const pillVariant = defineStyle({
  ...pillCommon,
  bgColor: 'utility.200',
  color: 'utility.700',

  _dark: {
    background: 'whiteAlpha.200',
    color: 'whiteAlpha.700',
  },
});

const inversePillVariant = defineStyle({
  ...pillCommon,
  bgColor: 'whiteAlpha.300',
  color: 'whiteAlpha.900',

  _dark: {
    background: 'utility.200',
    color: 'utility.700',
  },
});

const vividPillVariant = defineStyle(props => {
  const { colorScheme } = props;

  return {
    ...pillCommon,
    bgColor: `${colorScheme}.500`,
    color: 'whiteAlpha.900',

    _dark: {
      background: `${colorScheme}.500`,
      color: 'whiteAlpha.900',
    },
  };
});

const indicatorVariant = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    ...pillCommon,
    bgColor: `${colorScheme}.100`,
    color: `${colorScheme}.800`,
    fontSize: 'xs',
    height: 6,
    ml: 0,
    minWidth: 6,
    gap: 0,
    px: 1,
    textAlign: 'center',

    '.indicator-number': {
      px: 0.5,
    },

    '.indicator-icon': {
      color: `${colorScheme}.500`,
      width: 4,

      _dark: {
        color: `${colorScheme}.300`,
      },
    },

    _dark: {
      bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),
      color: `${colorScheme}.200`,

      '.indicator-icon': {
        color: `${colorScheme}.300`,
      },
    },
  };
});

const colorfulVariant = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    bgColor: `${colorScheme}.100`,
    color: `${colorScheme}.800`,

    '.badge-icon': {
      color: `${colorScheme}.500`,

      _dark: {
        color: `${colorScheme}.300`,
      },
    },

    _dark: {
      bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),
      color: `${colorScheme}.200`,

      '.badge-icon': {
        color: `${colorScheme}.300`,
      },
    },
  };
});

const transparentVariant = defineStyle(props => {
  const { colorScheme } = props;

  return {
    bgColor: 'transparent',
    color: `${colorScheme}.800`,
    fontWeight: 'normal',
    px: 0,

    _dark: {
      bgColor: 'transparent',
      color: `${colorScheme}.100`,
    },
  };
});

export const badgeTheme: ComponentStyleConfig = defineStyleConfig({
  baseStyle,
  defaultProps: {
    size: 'sm',
  },
  sizes,
  variants: {
    pill: pillVariant,
    'inverse-pill': inversePillVariant,
    'vivid-pill': vividPillVariant,
    indicator: indicatorVariant,
    colorful: colorfulVariant,
    transparent: transparentVariant,
  },
});
